import type { DaySchedule, SlotData, UnavailabilityMark } from "../lib/dayGrid"
import type { SlotDecoration } from "../lib/rescheduleTargets"
import type { StaffColumn } from "../lib/staffColumns"
import { SLOT_TYPES, slotTypeOf } from "../lib/slotTypes"
import type { SlotType } from "../lib/slotTypes"
import { SlotCell } from "./SlotCell"

/**
 * The day: a column per team member, a row per slot, in time order.
 *
 * Every column has the same rows, so the time labels are read off the first one — `buildDayGrid`
 * guarantees that, which is what lets one label column stand for all of them.
 */
export function DayView({ schedule, columns, columnUnavailability, onSlotClick, onColumnClick, bookable = false, bookableSlotTypes = SLOT_TYPES, loading = false, decorate }: {
  schedule: DaySchedule
  columns: StaffColumn[]
  /** Who is not working today, keyed by column id. Said once at the top of a column as well as on its rows. */
  columnUnavailability: Record<string, UnavailabilityMark>
  onSlotClick: (staffId: string, slotIdx: number) => void
  /** Whether free rows offer to be booked into — a booking permission, on a team Cal has slugged. */
  bookable?: boolean
  /**
   * The kinds of free row this user may book into (AB#779) — `bookableSlotTypes`. A free row of any
   * other kind is drawn, labelled, and not offered. Defaults to every kind.
   */
  bookableSlotTypes?: readonly SlotType[]
  /**
   * The day's appointments are still on their way. The headers and the time axis are drawn as
   * usual — who and when are known — and every cell as a placeholder until there is something to put
   * in it.
   */
  loading?: boolean
  /**
   * Opening one person, from the name at the top of their column — a column is one day of one
   * person, and the week is the same person over five. Omitted leaves the headers as labels.
   */
  onColumnClick?: (column: StaffColumn) => void
  /**
   * Selection mode (the manual reschedule workspace, AB#811): what each cell is for the booking being
   * placed — valid, invalid with a reason, a pending pairing, or the booking's own slot — passed to the
   * cell as `selection`. Undefined for a cell leaves it as it is; omitted draws the day as it always has.
   */
  decorate?: (columnId: string, slotIdx: number, cell: SlotData) => SlotDecoration | undefined
}) {
  if (columns.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-8 text-sm text-muted-foreground">
        This team has no members yet.
      </div>
    )
  }

  const rows = schedule[columns[0]!.id] ?? []

  if (rows.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-8 text-sm text-muted-foreground">
        Nothing scheduled for this day.
      </div>
    )
  }

  const gridTemplateColumns = `72px repeat(${columns.length}, minmax(140px, 1fr))`

  return (
    <div className="day-view-wrap" aria-busy={loading}>
      <div style={{ overflowX: "auto" }}>
        <div style={{ display: "grid", gridTemplateColumns, gap: 6 }}>
          {/* Member headers */}
          <div />
          {columns.map(member => {
            const away = columnUnavailability[member.id]

            // A button only when there is somewhere to go: a header that looks clickable and does
            // nothing is worse than one that never offered.
            const Header = onColumnClick ? "button" : "div"
            return (
              <Header
                key={member.id}
                className="staff-col-header"
                style={{ background: member.color, width: "100%" }}
                {...(onColumnClick
                  ? {
                    type: "button" as const,
                    title: `Open ${member.name}'s week`,
                    onClick: () => onColumnClick(member),
                  }
                  : {})}
              >
                <div style={{ fontSize: "1rem" }}>{member.initials}</div>
                <div style={{ fontSize: "0.78rem", marginTop: 2 }}>{member.name}</div>
                {/* An invited-but-unaccepted member takes no bookings, so an empty column is expected. */}
                {!member.accepted && (
                  <div style={{ fontSize: "0.62rem", marginTop: 2, opacity: 0.85 }}>Invite pending</div>
                )}
                {/* Said at the top as well as on every row, so someone scanning the header row of a
                    five-column day sees who is in without reading down each column. */}
                {away && (
                  <div style={{ fontSize: "0.62rem", marginTop: 2, opacity: 0.85 }}>
                    {away.label}
                  </div>
                )}
              </Header>
            )
          })}

          {/* Time rows */}
          {rows.map((row, slotIdx) => (
            <div key={row.startMinutes} style={{ display: "contents" }}>
              <div className="time-label">{row.time}</div>
              {columns.map(member => {
                const cell = schedule[member.id]?.[slotIdx]
                if (!cell) return <div key={`${member.id}-${slotIdx}`} />
                return (
                  <SlotCell
                    key={`${member.id}-${slotIdx}`}
                    cell={cell}
                    staff={member}
                    // An invited member who has not accepted hosts nothing, so a free row in their
                    // column is not a slot anybody can be booked into.
                    bookable={bookable && member.accepted && bookableSlotTypes.includes(slotTypeOf(cell))}
                    loading={loading}
                    selection={decorate?.(member.id, slotIdx, cell)}
                    onClick={() => onSlotClick(member.id, slotIdx)}
                  />
                )
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
